import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Stethoscope, ShieldCheck, AlertCircle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSEO } from "@/lib/seo";
import { isDemoMode } from "@/components/DemoBanner";

export default function TherapistApplication() {
  const [data, setData] = useState({
    full_name: "",
    credentials: "CCC-SLP",
    asha_number: "",
    state_license_number: "",
    license_state: "",
    bio: "",
    specialties: "",
    sliding_scale: false,
    contact_email: "",
    contact_email_public: true,
    website: "",
  });
  const [existing, setExisting] = useState(null);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);

  useSEO({ title: "Apply to be listed", description: "Apply to join the SonSpeak clinician directory.", noindex: true });

  useEffect(() => {
    (async () => {
      try {
        const { data: r } = await api.get("/therapist-directory/me");
        if (r.application) {
          setExisting(r.application);
          setData({
            ...r.application,
            specialties: (r.application.specialties || []).join(", "),
          });
        }
      } catch {/* fresh */}
    })();
  }, []);

  const update = (patch) => setData((d) => ({ ...d, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null); setInfo("");
    try {
      const body = {
        ...data,
        specialties: (data.specialties || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
          .slice(0, 10),
      };
      const { data: r } = await api.post("/therapist-directory/apply", body);
      setExisting(r.application);
      setInfo(r.resubmitted
        ? "Resubmitted — your changes will be reviewed by SonSpeak before going live."
        : "Application submitted! We'll review and approve within a few days. You'll be back to pending if you change anything.");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.findings) setError(detail);
      else setError({ message: formatApiErrorDetail(detail) || e.message });
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen dotted-bg">
      <header className="px-4 sm:px-6 py-3 border-b-[3px] border-[color:var(--ink)] bg-[color:var(--cream)] flex items-center gap-3">
        <Link to="/therapists" className="pill-btn"><ArrowLeft size={16} /> Back</Link>
        <div className="display text-lg sm:text-xl font-extrabold">Therapist application</div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        {isDemoMode() && (
          <div className="brutal-card bg-[color:var(--gold)] p-3 mb-5 text-sm" data-testid="therapist-app-demo-notice">
            <strong>Early access.</strong> This directory is a planned production feature.
            Real verification against ASHA / state-board records is not happening yet —
            applications submitted here are exploratory only.
          </div>
        )}

        <section className="brutal-card bg-[color:var(--cream-deep)] p-5 mb-5 flex items-start gap-3">
          <Stethoscope size={22} className="shrink-0 text-[color:var(--coral-deep)] mt-1" />
          <div>
            <h1 className="display text-2xl font-extrabold leading-tight">Get listed in the SonSpeak directory</h1>
            <p className="text-sm text-[color:var(--ink-soft)] mt-1">
              Each profile is hand-reviewed against ASHA / state-board records before going public. This protects parents — and gets you in front of the families looking for trusted, family-priced care.
            </p>
          </div>
        </section>

        {existing && (
          <div className={`brutal-card mb-5 p-3 text-sm ${
            existing.status === "approved" ? "bg-[#E6F8E1]" :
            existing.status === "rejected" ? "bg-[#FFE3E0]" : "bg-[color:var(--gold)]"
          }`} data-testid="therapist-app-status">
            <div className="font-bold capitalize">Status: {existing.status}</div>
            {existing.status === "rejected" && existing.rejection_reason && (
              <div className="text-xs mt-1">Reviewer note: {existing.rejection_reason}</div>
            )}
            {existing.status === "approved" && (
              <div className="text-xs mt-1">Live at <code>/therapists/{existing.slug}</code></div>
            )}
          </div>
        )}

        <form onSubmit={submit} className="brutal-card bg-white p-5 space-y-4" data-testid="therapist-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-semibold">Full name</span>
              <input className="brutal-input w-full mt-1" value={data.full_name} onChange={(e) => update({ full_name: e.target.value })} maxLength={80} required data-testid="therapist-full-name" />
            </label>
            <label className="block">
              <span className="text-sm font-semibold">Credentials</span>
              <input className="brutal-input w-full mt-1" value={data.credentials} onChange={(e) => update({ credentials: e.target.value })} placeholder="CCC-SLP, BCBA, OTR/L" required data-testid="therapist-credentials" />
            </label>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-semibold">ASHA # (optional)</span>
              <input className="brutal-input w-full mt-1" value={data.asha_number} onChange={(e) => update({ asha_number: e.target.value })} maxLength={24} data-testid="therapist-asha" />
            </label>
            <label className="block">
              <span className="text-sm font-semibold">State license #</span>
              <input className="brutal-input w-full mt-1" value={data.state_license_number} onChange={(e) => update({ state_license_number: e.target.value })} maxLength={40} required data-testid="therapist-license" />
            </label>
          </div>

          <label className="block">
            <span className="text-sm font-semibold">License state</span>
            <input className="brutal-input w-full mt-1" value={data.license_state} onChange={(e) => update({ license_state: e.target.value })} placeholder="e.g. California or CA" required data-testid="therapist-license-state" />
          </label>

          <label className="block">
            <span className="text-sm font-semibold">Bio (40–1500 chars — public)</span>
            <textarea rows={6} className="brutal-input w-full font-sans mt-1" value={data.bio} onChange={(e) => update({ bio: e.target.value })} maxLength={1500} required data-testid="therapist-bio" />
            <div className="text-xs text-[color:var(--ink-soft)] mt-1 text-right">{data.bio.length}/1500</div>
          </label>

          <label className="block">
            <span className="text-sm font-semibold">Specialties (comma-separated)</span>
            <input className="brutal-input w-full mt-1" value={data.specialties} onChange={(e) => update({ specialties: e.target.value })} placeholder="autism, gestalt language, AAC, early intervention" data-testid="therapist-specialties" />
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={data.sliding_scale} onChange={(e) => update({ sliding_scale: e.target.checked })} className="w-5 h-5 accent-[color:var(--coral)]" data-testid="therapist-sliding" />
            <span className="text-sm">I offer sliding-scale fees</span>
          </label>

          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-sm font-semibold">Contact email</span>
              <input type="email" className="brutal-input w-full mt-1" value={data.contact_email} onChange={(e) => update({ contact_email: e.target.value })} required data-testid="therapist-contact-email" />
            </label>
            <label className="block">
              <span className="text-sm font-semibold">Website (optional)</span>
              <input type="url" className="brutal-input w-full mt-1" value={data.website || ""} onChange={(e) => update({ website: e.target.value })} placeholder="https://" data-testid="therapist-website" />
            </label>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={data.contact_email_public} onChange={(e) => update({ contact_email_public: e.target.checked })} className="w-5 h-5 accent-[color:var(--coral)]" />
            <span className="text-sm">Show my email on my public profile</span>
          </label>

          {error && (
            <div className="brutal-card bg-[#FFE3E0] text-[color:var(--coral-deep)] p-3" data-testid="therapist-app-error">
              <div className="flex items-center gap-2 font-bold mb-1"><AlertCircle size={14} /> {error.message || "Couldn't submit."}</div>
              {error.findings && <ul className="list-disc pl-5 text-sm">{error.findings.map((f, i) => <li key={i}>{f}</li>)}</ul>}
              {error.suggestion && <div className="text-sm mt-1 italic">{error.suggestion}</div>}
            </div>
          )}
          {info && <div className="brutal-card bg-[#E6F8E1] p-3 text-sm" data-testid="therapist-app-info">{info}</div>}

          <button type="submit" disabled={busy} className="pill-btn primary text-base py-3" data-testid="therapist-submit">
            <ShieldCheck size={16} /> {busy ? "Submitting…" : (existing ? "Resubmit for review" : "Apply to be listed")}
          </button>
        </form>
      </main>
    </div>
  );
}
